import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardV2 } from '../../../components/CardV2/CardV2';
import PrimaryButton from '../../../components/PrimaryButton/PrimaryButton';
import ListContainer from '../../../components/ListContainer/ListContainer';
import ListElement from '../../../components/ListElement/ListElement';
import ElementActions from '../../../components/ElementActions/ElementActions';
import IconButton from '../../../components/IconButton/IconButton';
import ConfirmationPopup from '../../../components/ConfirmationPopup/ConfirmationPopup';
import { Icon } from '../../../components/Icon/Icon';
import deleteIcon from '../../../assets/images/bin.svg';
import edit from '../../../assets/images/pen.svg';
import Switch from '../../../components/Switch/Switch';
import NoDataCTA from '../../../components/NoDataCTA/NoDataCTA';
import CommissionOfferCard from '../../../components/CommissionOfferCard/CommissionOfferCard';
import CommissionContractOffersAPI, { CommissionContractOfferDto } from '../../../api/CommissionContractOffersAPI';
import { toastService } from '../../../services/ToastService';

interface CommissionPaymentOffersSectionProps {
  editOpen: boolean;
  isCreatingFirst: boolean;
  onNewOffer: () => void;
  onEditOffer: (offer: CommissionContractOfferDto) => void;
  onCreateFirstOffer: () => void | Promise<void>;
  onOffersChange?: (count: number) => void;
  onRegisterReload?: (reload: () => Promise<void>) => void;
}

export default function CommissionPaymentOffersSection({
  editOpen,
  isCreatingFirst,
  onNewOffer,
  onEditOffer,
  onCreateFirstOffer,
  onOffersChange,
  onRegisterReload,
}: CommissionPaymentOffersSectionProps) {
  const { t } = useTranslation();
  const [offers, setOffers] = useState<CommissionContractOfferDto[]>([]);
  const [reorderedOffers, setReorderedOffers] = useState<CommissionContractOfferDto[]>([]);
  const [loading, setLoading] = useState(true);
  const onOffersChangeRef = useRef(onOffersChange);
  onOffersChangeRef.current = onOffersChange;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CommissionContractOffersAPI.list();
      const nextOffers = res.data || [];
      setOffers(nextOffers);
      setReorderedOffers(nextOffers);
      onOffersChangeRef.current?.(nextOffers.length);
    } catch (error) {
      console.error('Failed to load commission contract offers', { error });
      toastService.showToast(
        t('commission_payment_offers.load_error') || 'Could not load offers',
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    onRegisterReload?.(load);
  }, [load, onRegisterReload]);

  useEffect(() => {
    setReorderedOffers(offers);
  }, [offers]);

  const handleQuickToggle = async (offer: CommissionContractOfferDto) => {
    if (offer.id == null) return;
    try {
      await CommissionContractOffersAPI.update(offer.id, {
        ...offer,
        enabled: !offer.enabled,
      });
      await load();
    } catch (error) {
      console.error('Failed to toggle commission contract offer', { offerId: offer.id, error });
      toastService.showToast(
        t('commission_payment_offers.toggle_error') || 'Could not update offer',
        'error',
      );
    }
  };

  const handleDelete = async (offer: CommissionContractOfferDto) => {
    if (offer.id == null) return;
    try {
      await CommissionContractOffersAPI.delete(offer.id);
      toastService.showToast(t('commission_payment_offers.deleted') || 'Deleted', 'success');
      await load();
    } catch (error) {
      console.error('Failed to delete commission contract offer', { offerId: offer.id, error });
      toastService.showToast(t('commission_payment_offers.delete_error') || 'Delete failed', 'error');
    }
  };

  const handleOffersReorder = async (reorderedItems: CommissionContractOfferDto[]) => {
    setReorderedOffers(reorderedItems);
    const orderedIds = reorderedItems.map((item) => item.id!).filter((id) => id != null);
    try {
      await CommissionContractOffersAPI.reorder(orderedIds);
    } catch (error) {
      console.error('Failed to reorder commission contract offers', { orderedIds, error });
      toastService.showToast(
        t('commission_payment_offers.reorder_error') || 'Could not reorder',
        'error',
      );
      await load();
    }
  };

  return (
    <CardV2>
      <div className="p-4 space-y-4">
        <div>
          <h3 className="text-lg font-medium">
            {t('commission_payment_offers.title') || 'Offers'}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t('commission_payment_offers.subtitle') ||
              'Packages brides choose on the submit form. When enabled, an offer overrides default fees for that contract.'}
          </p>
        </div>

        {!loading && offers.length > 0 && (
          <PrimaryButton
            text={t('commission_payment_offers.add') || '+ Add offer'}
            fn={onNewOffer}
            isDisabled={editOpen || isCreatingFirst}
          />
        )}

        <div className="overflow-x-auto w-full min-w-0">
          <ListContainer
            isLoading={loading || isCreatingFirst}
            elements={reorderedOffers}
            enableDragAndDrop
            onMovedItem={handleOffersReorder}
            emptyListComponent={() => (
              <NoDataCTA
                headline={t('commission_payment_offers.empty.title') || 'Start receiving dresses'}
                subtext={
                  t('commission_payment_offers.empty.body') ||
                  'Add a Standard package with one click. You can edit duration, split, and fees afterward.'
                }
                primaryActionText={t('commission_payment_offers.empty.primary') || 'Add your first offer'}
                onPrimaryAction={() => {
                  if (!isCreatingFirst) {
                    void onCreateFirstOffer();
                  }
                }}
                secondaryActionText={t('commission_payment_offers.empty.secondary') || 'Create custom offer'}
                onSecondaryAction={onNewOffer}
              />
            )}
            toElementFn={(offer: CommissionContractOfferDto) => (
              <ListElement
                key={offer.id}
              >
                <ElementActions
                  actions={
                    <div className="flex space-x-3 items-center">
                      <Switch
                        id={`offer-${offer.id}-enabled`}
                        checked={offer.enabled !== false}
                        onChange={() => void handleQuickToggle(offer)}
                      />
                      <IconButton onClick={() => onEditOffer(offer)}>
                        <Icon url={edit} cssStyles="w-5" />
                      </IconButton>
                      <ConfirmationPopup
                        onConfirm={() => void handleDelete(offer)}
                        confirmText={t('global.confirm') || 'Confirm'}
                        title={`${t('global.remove') || 'Remove'}: ${offer.name}`}
                      >
                        <Icon url={deleteIcon} cssStyles="w-5" />
                      </ConfirmationPopup>
                    </div>
                  }
                >
                  <CommissionOfferCard offer={offer} onEdit={() => onEditOffer(offer)} />
                </ElementActions>
              </ListElement>
            )}
          />
        </div>
      </div>
    </CardV2>
  );
}
